import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  browserPopupRedirectResolver,
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

import { firebaseConfig, firebaseEmulatorConfig } from '@/src/config/env';

export const firebaseApp = getApps().length
  ? getApp()
  : initializeApp(firebaseConfig);

function isFragileIOSInAppBrowser() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/i.test(userAgent);
  const isFacebookOrInstagramWebview = /FBAN|FBAV|FBIOS|FB_IAB|Instagram/i.test(
    userAgent,
  );

  return isIOS && isFacebookOrInstagramWebview;
}

function initializePersistentAuth(): Auth {
  const persistence = isFragileIOSInAppBrowser()
    ? [browserLocalPersistence, browserSessionPersistence, inMemoryPersistence]
    : [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ];

  try {
    return initializeAuth(firebaseApp, {
      persistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);

    if (message.includes('already-initialized')) {
      return getAuth(firebaseApp);
    }

    throw error;
  }
}

export const auth = initializePersistentAuth();

if (firebaseEmulatorConfig.enabled) {
  connectAuthEmulator(
    auth,
    `http://${firebaseEmulatorConfig.host}:${firebaseEmulatorConfig.ports.auth}`,
    { disableWarnings: true },
  );
}

function shouldForceFirestoreLongPolling() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor;

  return (
    vendor.includes('Apple') &&
    userAgent.includes('Safari') &&
    !userAgent.includes('Chrome') &&
    !userAgent.includes('Chromium') &&
    !userAgent.includes('CriOS') &&
    !userAgent.includes('FxiOS') &&
    !userAgent.includes('EdgiOS')
  );
}

export const db = (() => {
  try {
    if (isFragileIOSInAppBrowser()) {
      return initializeFirestore(firebaseApp, {
        experimentalForceLongPolling: shouldForceFirestoreLongPolling(),
        localCache: memoryLocalCache(),
      });
    }

    return initializeFirestore(firebaseApp, {
      experimentalForceLongPolling: shouldForceFirestoreLongPolling(),
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch {
    return getFirestore(firebaseApp);
  }
})();
export const storage = getStorage(firebaseApp);

if (firebaseEmulatorConfig.enabled) {
  connectFirestoreEmulator(
    db,
    firebaseEmulatorConfig.host,
    firebaseEmulatorConfig.ports.firestore,
  );
  connectStorageEmulator(
    storage,
    firebaseEmulatorConfig.host,
    firebaseEmulatorConfig.ports.storage,
  );
}
